import "@fortawesome/fontawesome-svg-core/styles.css";
import "./style.css";

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { Settings } from "luxon";
import { createPinia } from "pinia";
import { createPersistedState } from "./pinia/plugins/persistedState";
import { useThemeStore } from "./pinia/theme";
import { ensureAccent } from "./utils/portalAccent";
import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import { registerBrandIcons } from "./utils/brandIcons";
import { startRouteWarmup } from "./utils/routeWarmup";
import { installBlurRemovedFields } from "./lib/blurRemovedFields";

// The catalogue less the admin portal's screens (i18nPortalPlugin.ts).
import messages from "virtual:user-messages";

import App from "./App.vue";

import { router, routes } from "./routes-user.ts";
import { installResponsePolicy } from "./services/responsePolicy.ts";
import { getClientFingerprint } from "./services/fingerprint";
import { createAppQueryClient, setAppQueryClient } from "./queries/client";
import {
  endUserSession,
  markSessionSlotUnavailable,
  markPasswordChangeRequired,
  restoreUserStatus,
  sessionKeys,
  signOutUser,
} from "./queries/session";
import { isNativePlatform } from "./capacitor/platform";
import { ensureIpChangeStarted } from "./capacitor/server-url";
import { initAppUpdater } from "./capacitor/app-updater";
import { initAuthCallback } from "./capacitor/auth-callback";
import {
  initBatteryOptimizationWatcher,
  restorePendingBatteryOptimizationPrompt,
} from "./capacitor/battery-optimization";
import { getSessionToken } from "./capacitor/session-token";
import { initExpiryNotifications } from "./capacitor/expiry-notifications";
import { initForceLogoutWatcher } from "./capacitor/force-logout";
import { initNotificationPermissionWatcher } from "./capacitor/notification-permissions";
import { initServiceHealthWatcher } from "./capacitor/service-health";

Settings.defaultLocale = Intl.DateTimeFormat(window.navigator.languages).resolvedOptions().locale;

const i18n = createI18n({
  legacy: false,
  locale: navigator.language,
  fallbackLocale: "en-AU",
  messages,
});

registerBrandIcons();

// `forget` removes keys left by stores that no longer exist.
const pinia = createPinia();
pinia.use(createPersistedState({ storage: "session", forget: ["user", "admin", "auth"] }));

const queryClient = createAppQueryClient({ statusKey: sessionKeys.user });
setAppQueryClient(queryClient);

const app = createApp(App)
  .use(router)
  .use(pinia)
  .use(i18n)
  .use(VueQueryPlugin, { queryClient })
  .component("font-awesome-icon", FontAwesomeIcon);

// Before anything renders, so no component paints in the admin red while the
// theme request is out (utils/portalAccent.ts).
ensureAccent(useThemeStore().theme);

// Wire response policy after pinia is active so the store is resolvable.
installResponsePolicy({
  router,
  session: { end: endUserSession, markSessionSlotUnavailable, markPasswordChangeRequired },
});
// Paint from the tab's last status; the guard re-reads it after three minutes.
restoreUserStatus();

// Start the client fingerprint now so it is usually ready by the first API call.
// No request waits for it: one sent earlier goes without it (services/fingerprint.ts).
void getClientFingerprint();

// A field removed while focused never blurs, which left password managers'
// inline menus on screen (lib/blurRemovedFields.ts).
installBlurRemovedFields();

startRouteWarmup(router, routes, queryClient);

// Mount once the first route resolves, so the shell's spinner covers its chunk.
async function mountWhenRouted() {
  try {
    await router.isReady();
  } catch {
    // Usually a chunk a deploy removed (utils/chunkReload.ts). Mount anyway.
  }
  app.mount("#app");
}

void mountWhenRouted();

if (isNativePlatform()) {
  const forceLogoutHandler = async () => {
    try {
      await signOutUser();
    } catch (e) {
      console.warn("[main-user] force logout: sign-out failed:", e);
    }
    await router.push({ name: "Login" });
  };

  initAuthCallback(router);
  initNotificationPermissionWatcher();
  initExpiryNotifications();
  ensureIpChangeStarted();
  initAppUpdater();

  // Android-only: OEM battery-optimisation prompt, foreground-service-killed
  // banner, and force-logout flag from the FGS notification action.
  initBatteryOptimizationWatcher();
  initServiceHealthWatcher();
  initForceLogoutWatcher(forceLogoutHandler);

  // Login surfaces the battery-optimisation overlay, then postLoginNavigate
  // does a full-page reload that wipes it from memory. Re-surface it here when
  // a session is present, so it survives that one reload.
  getSessionToken().then((token) => {
    if (token) restorePendingBatteryOptimizationPrompt();
  });
}
